import { z } from 'zod';
import { blankToNull } from './blankToNull';

// A person the delivery can be handed to — the titular or one of the authorized pickups. The same
// triple for both, because they are the same kind of thing seen from two sides.
const PersonSchema = z.object({
  id: z.number().nullish(),
  name: z.string().nullish(),
  phone: z.string().nullish(),
});

// The detail of a single «Entrega» as the panel renders its read-only card. Wire shape is
// snake_case (app-service / DRF); this parses it and exposes a camelCase domain object.
// It mirrors the CONFIRMED list fields (AS-025) plus detail-only additions (the location, the
// cerradura, the estimated expiration + its "extended" flag), and NOTHING the card does not
// render, so the panel never surfaces PII it did not ask for (data minimization). `status` is an
// open string (fail-open to the raw label); `tracking` and
// `carrier` are optional; `box` and the dates are nullable — a legacy parcel with no dates yields an
// empty timeline, and absent fields render an em dash rather than failing the parse. `blocked_date`
// (contract update 2026-07-03: the blocked state carries its date) is nullish — delivered as null
// when the delivery was never blocked, and tolerated absent so the older shape still parses. The
// six per-state transition dates (`rejected_date`, `cancelled_date`, `failed_date`,
// `retrieved_date`, `non_collected_date`, `non_retrieved_date`, added by CBX-10877 so every
// lifecycle state carries its own `*_date` instead of only living in `domain_events`) are
// mirrored the same way: nullish, delivered null for deliveries predating the backend deploy
// (no backfill), tolerated absent so a pre-deploy payload still parses.
//
// **Contract update 2026-08-18 (CBX-10954, ✅ delivered — the nested detail).** Three groups of flat
// first-level fields moved into the entity they describe, and the domain object below MIRRORS that
// nesting instead of re-flattening it — the panel's shape is the contract's shape, one less mapping
// to keep in someone's head:
//   · `lock` now travels INSIDE `box` (`box.lock`), because the cerradura belongs to the «buzón» and
//     was only ever reachable through it (delivery → box_assignment → box → lock, so «cerradura sin
//     buzón» was already irrepresentable — anidar loses nothing). `box.lock` is null when the box has
//     no paired cerradura (`Box.lock` is a nullable FK); `box` itself stays nullable.
//   · `locker_timezone` + `address` became `location: {timezone, address}`, an object that is
//     **always present** — hence required here, exactly as strict as the flat `locker_timezone` was.
//     `timezone` is the locker's IANA zone (ADR 0011) and keeps its backend-side `UTC` fallback;
//     `address` is its only nullable member (a location with no address row).
//   · `carrier` went from a bare string to `{id, name, allows_extension} | null`, ABSORBING the flat
//     `carrier_allows_extension` that CBX-11059 had just added. This is an INCOMPATIBLE flip: the old
//     flat payload no longer parses (fail-closed at the boundary — the detail shows its error state
//     rather than a card with half its fields blank), which is why the backend and panel deploys are
//     coordinated. `carrier` keeps the `nullish` tolerance it had as a string: the refactor changes
//     its SHAPE, not how forgiving the panel is about the key being absent. Its null is an orphan
//     `courier` row, not a real «entrega sin transportista» (`deliveries.carrier_id` is NOT NULL).
// **`carrier.id` is deliberately NOT mirrored.** The wire carries it — it is the same identity the
// carrier catalog and the ubicación filter cross on — but nothing in the DETAIL reads or renders it,
// and this schema models only what the card shows. Zod strips it; adding it the day a consumer needs
// it is a one-line change, whereas carrying it meanwhile is a field that can only rot.
//
// `is_return` and `carrier.allows_extension` are the two non-state gates of «Ampliar custodia», and
// they degrade in the OPPOSITE direction to everything above: absent, null or mistyped, both resolve
// to `null`, which the predicate reads as «offer the action». An action never offered is a feature
// that does not exist, whereas a wrongly offered one is answered by the backend's error code — so
// these two, and only these two, carry `.catch(null)` on top of `nullish()`. `nullish()` tolerates
// *absent*, not *mistyped*: this parse is total, so an `is_return: 0` from a legacy serializer path
// would throw and blank the entire «Entrega» detail through the sub-tab's error boundary. Every
// OTHER field must keep failing loudly when mistyped — a wrong type there is a contract bug, not a
// pending deploy. Note the gate now lives one level down, so a **null `carrier`** carries no policy
// at all: the consumer reads it as `carrier?.allowsExtension ?? null`, which is the same fail-open by
// construction. ⚠️ Proposed shape, unverified against app-service (AS-035).
//
// **Contract update 2026-08-19 (CBX-11159, ✅ delivered — the pickup authorship).** `collected_by_user`
// is a first-level nullable boolean saying whether the «Usuario» CONFIRMED the pickup: `true` = someone
// pressed confirm from their own session (`Pickup.confirm`), `false` = the scheduled auto-confirmation
// closed it (`Pickup.auto_confirm`), `null` = not attributable — an uncollected delivery, a collection
// that left no pickup (a Customer-Care-forced transition creates none, and neither did the legacy
// `complete_collection`), or any pickup predating the deploy, since the datum is FORWARD-ONLY with no
// backfill. The semantics are by CONFIRMATION, not by credential: an opening with the «Usuario»'s own
// código that nobody confirms in the app is `false`, and «their credentials opened the buzón» is a
// sentence no persisted datum backs. It is the THIRD field here to carry `.catch(null)` and the first
// for the OPPOSITE reason to the other two: `is_return` and `carrier.allows_extension` swallow a
// mistyped value so an ACTION KEEPS BEING OFFERED, whereas this one swallows it so a purely COSMETIC
// CAPTION can never blank the entire detail through the sub-tab's error boundary. Nothing renders it
// but one caption on the trazabilidad's «Recogido» row, so its worst failure must be «no line».
// Deliberately NOT mirrored alongside it: any actor identity (name, id, email, the CC agent) — the
// request asked for a boolean and nothing else, and Zod's strip is what enforces it.
//
// **Contract update 2026-09-04 (CBX-11313, ✅ DELIVERED — AS-052 confirmed as proposed).**
// The detail now carries WHO the parcel is for: `recipient`, the titular, and `shared_with`, the
// people it was authorized to be handed to. This is the first read here to carry THIRD-PARTY PII —
// an authorized pickup is not the subject of the query — so the field set stops at `{id, name, phone}`
// per person, and two independent controls keep it there: Zod strips whatever the object schema does
// not declare, and the transform builds each person field by field instead of spreading the parsed
// object. Either one alone is enough; both are free.
//
// **BOTH ids here are LOAD-BEARING**, unlike the deliberately unmirrored `carrier.id`.
//
// `shared_with[].id` separates two absences that would otherwise read the same: `{id: <n>, name: null}`
// is «there IS an account and its row could not be read» — the semantics AS-039 already fixed for the
// titular — whereas `{id: null, name: null}` is «the parcel was authorized to a phone with no account
// at all». Those two facts owe the reader different words, so the schema keeps them apart.
//
// `recipient.id` became load-bearing with «Cambiar titular», and it is the only id here that leaves
// the panel again: it is the `expected_current_user_id` of that write's concurrency guard — the
// claim «this is who the card showed me», which backend compares under `SELECT … FOR UPDATE` and
// refuses if another agent moved the titular first — and it is what the dialog compares against to
// refuse handing the parcel to the account that already holds it. Mind the type boundary when
// touching it: here it is a number and a «Usuario» search hit carries it as a string, so the
// comparison is normalized in exactly one place (`recipientEligibility` in the care app).
//
// **This schema is DELIBERATELY LOOSER than the contract guarantees, and that is not drift.**
// Backend delivers `shared_with` as a list that is never null, and an authorized pickup's `phone` as
// NOT NULL (it is `shared_deliveries.target_phone`, the number the share was sent to — the identity
// of the authorization itself). The titular's `phone` IS nullable, because there the only source is
// the same `clientes.phone_number` as AS-039. Modelling those guarantees strictly would buy
// fail-loud detection at a price this read cannot pay: it runs with `throwOnError: true`, so a
// single missing key would take down the WHOLE sub-tab — information card, trazabilidad and actions
// included — through its error boundary, in any environment whose app-service deploy lags the
// panel's. One section going quiet is the cheaper failure. Tighten this the day the payload has been
// read in EVERY environment, not before.
//
// Which leaves the standing risk unpaid for, and it is worth naming: a key delivered under a
// different name parses just as quietly and would leave the section invisible forever, with no error
// and no alert. There is no continuous detector — the compensating control is a ONE-OFF check of a
// REAL payload per environment, never a check that this schema parses.
//
// Two delivered semantics the panel depends on and does not enforce, because they are resolved
// server-side: only PENDING and ACCEPTED shares travel (somebody who declined is not someone who can
// pick the parcel up, and no `status` rides the wire for the panel to filter on), and the list
// arrives ordered by `created_at` DESCENDING — most recent share first, `id` breaking ties — with
// duplicates preserved, because two shares to the same phone are two real authorizations.
//
// **Contract update 2026-09-07 (CBX-11337, requested — the location's own id).** `location` gains
// `id`: the integer the «Entregas» list of a ubicación is indexed by, so the detail can open that
// list for its own building instead of making the agent find it again by address. The address is
// NOT a key — the ubicación search returns one row per PORTAL and the text repeats legitimately, so
// resolving a building by free text can open the wrong one with nothing on screen saying so.
// `nullish` because app-service publishes the field AFTER this panel release: absent normalizes to
// `null` and the shortcut simply is not drawn, which is what lets this ship before the field exists.
// It is the FOURTH field here to carry `.catch(null)`, and `.positive()` sits under it because the
// failure being bought off is not cosmetic like `collected_by_user`'s: a `0` passes `.int()` and
// would travel into a request path as if it named a real ubicación. An unusable id must switch the
// shortcut OFF, never empty the whole detail through the sub-tab's error boundary.
export const DeliveryDetailSchema = z
  .object({
    id: z.string().min(1),
    status: z.string(),
    tracking: z.string().nullish(),
    carrier: z
      .object({ name: z.string(), allows_extension: z.boolean().nullish().catch(null) })
      .nullish(),
    box: z
      .object({
        id: z.string(),
        number: z.string(),
        lock: z
          .object({ id: z.string().nullish(), qr: z.string(), model: z.string().nullable() })
          .nullable(),
      })
      .nullable(),
    location: z.object({
      id: z.number().int().positive().nullish().catch(null),
      timezone: z.string(),
      address: z.string().nullable(),
    }),
    stored_date: z.string().nullable(),
    collected_date: z.string().nullable(),
    blocked_date: z.string().nullish(),
    rejected_date: z.string().nullish(),
    cancelled_date: z.string().nullish(),
    failed_date: z.string().nullish(),
    retrieved_date: z.string().nullish(),
    non_collected_date: z.string().nullish(),
    non_retrieved_date: z.string().nullish(),
    estimated_expiration_date: z.string().nullable(),
    is_extended_expiration_date: z.boolean(),
    is_return: z.boolean().nullish().catch(null),
    collected_by_user: z.boolean().nullish().catch(null),
    recipient: PersonSchema.nullish(),
    shared_with: z.array(PersonSchema).nullish(),
  })
  .transform((d) => ({
    id: d.id,
    status: d.status,
    tracking: d.tracking ?? null,
    carrier:
      d.carrier == null
        ? null
        : { name: d.carrier.name, allowsExtension: d.carrier.allows_extension ?? null },
    box:
      d.box === null
        ? null
        : {
            id: d.box.id,
            number: d.box.number,
            lock:
              d.box.lock === null
                ? null
                : { id: d.box.lock.id ?? null, qr: d.box.lock.qr, model: d.box.lock.model },
          },
    location: {
      id: d.location.id ?? null,
      timezone: d.location.timezone,
      address: d.location.address,
    },
    storedDate: d.stored_date,
    collectedDate: d.collected_date,
    blockedDate: d.blocked_date ?? null,
    rejectedDate: d.rejected_date ?? null,
    cancelledDate: d.cancelled_date ?? null,
    failedDate: d.failed_date ?? null,
    retrievedDate: d.retrieved_date ?? null,
    nonCollectedDate: d.non_collected_date ?? null,
    nonRetrievedDate: d.non_retrieved_date ?? null,
    estimatedExpirationDate: d.estimated_expiration_date,
    isExtendedExpirationDate: d.is_extended_expiration_date,
    isReturn: d.is_return ?? null,
    collectedByUser: d.collected_by_user ?? null,
    recipient: d.recipient
      ? {
          id: d.recipient.id ?? null,
          name: blankToNull(d.recipient.name),
          phone: blankToNull(d.recipient.phone),
        }
      : null,
    sharedWith: (d.shared_with ?? []).map((p) => ({
      id: p.id ?? null,
      name: blankToNull(p.name),
      phone: blankToNull(p.phone),
    })),
  }));

export type DeliveryDetail = z.infer<typeof DeliveryDetailSchema>;
